import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { FlexibleTracking } from './FlexibleTracking';
import { accountingService } from '../services/accountingService';
import { signatureService } from '../services/signatureService';
import { userService } from '../services/userService';
import { settingsService } from '../services/settingsService';
import { useAuth } from '../contexts/AuthContext';
import { hasUserRole } from '../utils/userRoles';

const FINANCE_FLOW_OPTIONS = [
  { value: 'invoice-waiting', label: 'Fatura Bekliyor' },
  { value: 'invoice-approval-pending', label: 'Fatura Kontrol' },
  { value: 'invoice-check-approved', label: 'Fatura Onay' },
  { value: 'payment-pending', label: 'Muhasebeye Aktarıldı' },
] as const;

const DEFAULT_VISIBLE_COLUMNS = [
  'sevkKodu',
  'ettnNo',
  'yuklemeIli',
  'bosaltmaIli',
  'surucuAdi',
  'cekici',
  'dorse',
  'invoiceNumber',
  'preApprovalPdfPath',
  'teslimNoktasiSayisi',
  'ugrmaSayisi',
  'bazFiyat',
  'hedefFiyat',
  'durakBedeli',
  'beklemeBedeli',
  'hammaliye',
  'toplamBedel',
  'hizmetTuru',
  'paymentControllerInfo',
  'paymentApproverInfo',
  'mailAtildi',
  'mailGonderimTarihi',
  'eSignAction',
  'teslimEvraklari',
  'iadeTutanaklari',
  'faturalar',
  'navlun',
  'faturaBasligi',
  'accountingStatus',
  'createdAt',
] as const;

const normalizeAccountingStatus = (item: any) => {
  const raw = String(item?.accountingStatus || item?.accounting_status || '').trim();
  const signStatus = String(item?.paymentPreApprovalSignStatus || item?.payment_pre_approval_sign_status || '').trim();
  const hasControllerSign = Boolean(item?.paymentControllerAt || item?.payment_controller_at);
  const hasFinalSign = Boolean(item?.paymentApprovedAt || item?.payment_approved_at);
  const hasControllerESign = signStatus === 'signed' && hasControllerSign && !hasFinalSign;
  if (!raw || raw === 'none') return 'invoice-waiting';
  if (raw === 'delivery-approval-pending' || raw === 'delivery-rejected') return 'invoice-waiting';
  if (raw === 'invoice-check-pending') return 'invoice-approval-pending';
  if ((raw === 'invoice-approval-pending' || raw === 'payment-pre-approval' || raw === 'invoice-check-approved') && hasControllerESign) return 'invoice-check-approved';
  if (raw === 'payment-pre-approval' && !hasControllerESign) return 'invoice-approval-pending';
  return raw;
};

const parseIntermediateDeliveryStops = (value: any): Array<{ label?: string; customerName?: string }> => {
  if (!value || value === '-') return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') {
    if (Array.isArray(value.delivery)) return value.delivery;
    return [];
  }
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed || trimmed === '-') return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.delivery)) return parsed.delivery;
  } catch {
    return trimmed
      .replace(/^(Boşaltma:|Bosaltma:|Yükleme:|Yukleme:)/i, '')
      .split(/[,|]/)
      .map((part) => ({ label: String(part || '').trim() }))
      .filter((part) => part.label);
  }
  return [];
};

const buildUniqueLocationList = (values: Array<string | null | undefined>, fallback = '-') => {
  const unique = Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
  return unique.length > 0 ? unique.join(', ') : fallback;
};

const getRoutePointLocationSummary = (item: any, field: 'city' | 'district', fallbackValue?: string | null) => {
  const routePoints = Array.isArray(item?.route_points) ? item.route_points : [];
  const unloadingValues = routePoints
    .filter((point: any) => String(point?.operation_type || '').trim().toLocaleLowerCase('tr-TR') === 'unloading')
    .map((point: any) => point?.[field]);
  const intermediateValues = parseIntermediateDeliveryStops(item?.araNoktalar || item?.ara_noktalar || item?.intermediate_stops).map((stop: any) => stop?.[field]);
  return buildUniqueLocationList([fallbackValue, ...unloadingValues, ...intermediateValues]);
};

const buildDeliveryPointsText = (deliveryPoint?: string | null, intermediateStops?: any) => {
  const mainPoint = String(deliveryPoint || '').trim() || '-';
  const labels = Array.from(new Set([
    mainPoint,
    ...parseIntermediateDeliveryStops(intermediateStops).map((stop) => String(stop?.label || stop?.customerName || '').trim()).filter(Boolean),
  ].filter(Boolean)));
  return labels.length > 0 ? labels.join(' | ') : '-';
};

const getIntermediateStopCount = (deliveryPoint?: string | null, intermediateStops?: any) => {
  const labels = buildDeliveryPointsText(deliveryPoint, intermediateStops)
    .split('|')
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  return Math.max(0, labels.length - 1);
};

const resolveAccountingFinancialValues = (item: any, autoCalculateStopFees = true) => {
  const navlun = Number(item.price || item.fixed_price || item.final_price || item.base_price || 0);
  const durakBedeli = autoCalculateStopFees ? Number(item.ugramaFee || item.ugrama_fee || item.ugrama_bedeli || 0) : 0;
  const beklemeBedeli = Number(item.beklemeFee || item.bekleme_bedeli || item.bekleme_fee || item.waitingFee || item.waiting_fee || 0);
  const hammaliye = Number(item.hammaliyeFee || item.hammaliye_bedeli || item.hammaliye_fee || 0);
  const toplamBedel = navlun + durakBedeli + beklemeBedeli + hammaliye;
  return { navlun, durakBedeli, beklemeBedeli, hammaliye, toplamBedel };
};

const mapAccountingRowToTrackingRow = (item: any, index: number, autoCalculateStopFees = true) => {
  const accountingStatus = normalizeAccountingStatus(item);
  const canonicalDeliveryPointsText = String(item.delivery_points_text || '').trim();
  const deliveryPointsText = canonicalDeliveryPointsText || buildDeliveryPointsText(item.deliveryName || item.delivery_name || '-', item.araNoktalar || item.ara_noktalar || item.intermediate_stops || '-');
  const { navlun, durakBedeli, beklemeBedeli, hammaliye, toplamBedel } = resolveAccountingFinancialValues(item, autoCalculateStopFees);
  const deliveryPointCount = Math.max(1, deliveryPointsText.split('|').map((value) => value.trim()).filter(Boolean).length, Number(item.stop_count || 0));
  const extraStopCount = Math.max(0, deliveryPointCount - 1);
  const basePrice = Number(item.basePrice || item.base_price || item.matchedBasePriceSnapshot || item.matched_base_price_snapshot || 0);

  return {
    rowKey: [String(item.id || ''), String(item.shipmentNo || item.shipment_no || ''), 'finance', String(index)].join('::'),
    id: String(item.id || ''),
    archiveCode: '-',
    folderName: '-',
    invoiceNumber: item.invoiceNo || item.invoice_no || item.fatura_no || item.invoiceCode || item.invoice_code || '-',
    ettnNo: item.ettnNo || item.ettn_no || '-',
    invoiceGroupKey: item.invoiceGroupKey || item.invoice_group_key || null,
    invoiceGroupCount: Number(item.invoiceGroupCount || item.invoice_group_count || 0),
    folderNumber: '',
    evrakSiraNo: '',
    sevkKodu: item.shipmentNo || item.shipment_no || '-',
    planKodu: item.plan_code || item.planCode || item.plan_kodu || '-',
    cekici: item.vehiclePlate || item.cekici_plaka || '-',
    dorse: item.trailerPlate || item.dorse_plaka || '-',
    surucuAdi: item.driverName || item.driver_name || '-',
    surucuKonumu: '-',
    irtibatTelefon: item.contactPhone || '-',
    surucuTelefon: item.driverPhone || item.driver_phone || '-',
    surucuGunlukSureKalani: '-',
    surucuHaftalikSureKalani: '-',
    surucuIkiHaftalikSureKalani: '-',
    gunlukKm: 0,
    tcKimlikNo: '-',
    faturaBasligi: item.invoiceTitle || item.invoice_title || '-',
    disNakliye: 'Hayır',
    yuklemeMusterisi: item.pickupName || item.pickup_name || '-',
    bosaltmaMusterisi: deliveryPointsText,
    deliveryPointsText,
    yuklemeIli: item.pickupCity || item.pickup_city || '-',
    yuklemeIlcesi: item.pickupDistrict || item.pickup_district || '-',
    bosaltmaIli: getRoutePointLocationSummary(item, 'city', item.deliveryCity || item.delivery_city || '-'),
    teslimIlcesi: getRoutePointLocationSummary(item, 'district', item.deliveryDistrict || item.delivery_district || '-'),
    yuklemeAdres: item.pickupAddress || item.pickup_address || '-',
    teslimAdres: item.deliveryAddress || item.delivery_address || '-',
    aracAtamaTarihi: item.assigned_at || '',
    yuklemeTarihi: item.yukleme_talep_tarihi || item.pickupDate || item.pickup_date || '',
    terminTarihi: item.paymentDueDate || item.payment_due_date || item.deliveryApprovalDeadlineAt || item.delivery_approval_deadline_at || '',
    randevuluTeslim: 'Standart',
    teslimNoktasiSayisi: deliveryPointCount,
    ugrmaSayisi: extraStopCount,
    navlun,
    bazFiyat: basePrice,
    hedefFiyat: Number(item.targetPrice || item.target_price || (basePrice + durakBedeli) || 0),
    durakBedeli,
    beklemeBedeli,
    bekleme_bedeli: beklemeBedeli,
    beklemeFee: beklemeBedeli,
    waitingFee: beklemeBedeli,
    demurrageManualEdit: item.demurrage_manual_edit != null ? Number(item.demurrage_manual_edit) : 0,
    seferKm: 0,
    tlKm: 0,
    hammaliye,
    hammaliye_bedeli: hammaliye,
    hammaliyeFee: hammaliye,
    toplamBedel,
    lojistikTuru: String(item.transport_model || '').trim() === 'bid' ? 'İhale' : 'Spot',
    hizmetTuru: item.yuk_tipi || item.loadType || item.load_type || item.hizmet_turu || '-',
    istenenKasaTipi: item.assigned_vehicle_type || item.vehicle_type || '-',
    yukAgirlik: Number(item.weight_kg || 0),
    yukHacim: Number(item.volume_m3 || 0),
    palletCount: Number(item.pallet_count || 0),
    operasyonNot: item.deliveryRejectReason || item.delivery_reject_reason || '-',
    araNoktalar: '-',
    nearestHub: '-',
    yuklemeEvraklari: '',
    teslimEvraklari: '',
    iadeTutanaklari: '',
    faturalar: item.invoiceDocument?.fileName || item.invoiceDocument?.filePath || item.invoiceFileName || item.invoice_file_name || item.invoiceFilePath || item.invoice_file_path || item.invoiceCode || item.invoice_code || '',
    invoiceDocumentId: item.invoiceDocumentId || item.invoice_document_id || null,
    invoiceFilePath: item.invoiceDocument?.filePath || item.invoiceFilePath || item.invoice_file_path || item.invoiceDocument?.fileName || item.invoiceFileName || item.invoice_file_name || '',
    islemDurum: accountingStatus,
    durum: 'faturalandirildi',
    accountingStatus,
    preApprovalPdfPath: item.preApprovalDocument?.filePath || item.pre_approval_file_path || '',
    preApprovalPdfPageCount: item.preApprovalPdfPageCount || null,
    paymentControllerInfo: item.paymentControllerName || item.payment_controller_name || '-',
    paymentControllerAt: item.paymentControllerAt || item.payment_controller_at || '',
    paymentControllerPdfPath: item.signedPreApprovalDocument?.filePath || item.signed_pre_approval_file_path || item.preApprovalDocument?.filePath || item.pre_approval_file_path || '',
    paymentApproverInfo: item.paymentApproverName || item.payment_approver_name || '-',
    paymentApproverPdfPath: item.signedPreApprovalDocument?.filePath || item.signed_pre_approval_file_path || '',
    paymentApprovedAt: item.paymentApprovedAt || item.payment_approved_at || '',
    mailAtildi: item.signedPdfEmailedAt || item.signed_pdf_emailed_at ? 'Evet' : 'Hayır',
    mailGonderimTarihi: item.signedPdfEmailedAt || item.signed_pdf_emailed_at || '',
    eSignStatus: item.paymentPreApprovalSignStatus || item.payment_pre_approval_sign_status || '-',
    createdAt: item.paymentCompletedAt || item.payment_completed_at || item.paymentDueDate || item.payment_due_date || item.invoiceUploadedAt || item.invoice_uploaded_at || item.deliveryCompletedAt || item.delivery_completed_at || item.created_at || '',
    updatedAt: item.updated_at || '',
    pickupWarehouseId: item.pickupWarehouseId || item.pickup_warehouse_id || null,
  };
};

export function AccountingWorkflow() {
  const { user } = useAuth();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [trackingScope, setTrackingScope] = useState<{ allowedPickupWarehouseIds: string[]; canViewNonWarehousePickups: boolean }>({ allowedPickupWarehouseIds: [], canViewNonWarehousePickups: false });
  const [signatureModal, setSignatureModal] = useState<{ shipmentId: string; shipmentNo: string; requestId: string; token: string; status: string; signingRole: 'controller' | 'approver'; error?: string | null; pageCount?: number | null } | null>(null);
  const [bulkEmailModalOpen, setBulkEmailModalOpen] = useState(false);
  const [bulkResetModal, setBulkResetModal] = useState<{ count: number } | null>(null);
  const [bulkESignModal, setBulkESignModal] = useState<{ count: number; role: 'controller' | 'approver' } | null>(null);
  const [bulkEmailRows, setBulkEmailRows] = useState<any[]>([]);
  const [bulkEmailRecipientEmail, setBulkEmailRecipientEmail] = useState('');
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  const [repairingInvoiceLinks, setRepairingInvoiceLinks] = useState(false);
  const [scanningEttn, setScanningEttn] = useState(false);
  const [accountingViewTab, setAccountingViewTab] = useState<'all' | 'duplicates'>('all');
  const [autoCalculateStopFees, setAutoCalculateStopFees] = useState(true);
  const autoGeneratingPreApprovalIdsRef = useRef<Set<string>>(new Set());
  const signStatusRefreshInFlightRef = useRef(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [response, systemSettings] = await Promise.all([
        accountingService.getOverview(),
        settingsService.getSettingValue('settings_system_preferences', { autoCalculateStopFees: true }).catch(() => ({ autoCalculateStopFees: true })),
      ]);
      const nextAutoCalculateStopFees = (systemSettings as any)?.autoCalculateStopFees !== false;
      setAutoCalculateStopFees(nextAutoCalculateStopFees);
      const items = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      setRows(items.map((item: any, index: number) => mapAccountingRowToTrackingRow(item, index, nextAutoCalculateStopFees)));

      const missingPreApprovalRows = items.filter((item: any) => {
        const accountingStatus = normalizeAccountingStatus(item);
        const hasPreApprovalPdf = Boolean(item.preApprovalDocument?.filePath || item.pre_approval_file_path);
        const rowId = String(item.id || '');
        return accountingStatus === 'invoice-approval-pending' && !hasPreApprovalPdf && rowId && !autoGeneratingPreApprovalIdsRef.current.has(rowId);
      });

      if (missingPreApprovalRows.length > 0) {
        for (const item of missingPreApprovalRows) {
          const rowId = String(item.id || '');
          autoGeneratingPreApprovalIdsRef.current.add(rowId);
          try {
            await accountingService.generatePreApprovalPackage(rowId);
          } catch (error) {
            console.error(`Pre-approval PDF auto-generation failed for shipment ${rowId}:`, error);
          } finally {
            autoGeneratingPreApprovalIdsRef.current.delete(rowId);
          }
        }

        const refreshedResponse: any = await accountingService.getOverview();
        const refreshedItems = Array.isArray(refreshedResponse?.data) ? refreshedResponse.data : Array.isArray(refreshedResponse) ? refreshedResponse : [];
        setRows(refreshedItems.map((item: any, index: number) => mapAccountingRowToTrackingRow(item, index, nextAutoCalculateStopFees)));
      }
    } catch (error) {
      console.error('Accounting workflow load failed:', error);
      toast.error('Finansal işlemler verileri alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    if (!user?.id || hasUserRole(user, 'admin')) return;
    userService.getUser(user.id).then((response: any) => {
      const item = response?.data || {};
      const allowedPickupWarehouseIds = Array.isArray(item.allowed_pickup_warehouse_ids)
        ? item.allowed_pickup_warehouse_ids.map(String)
        : typeof item.allowed_pickup_warehouse_ids === 'string'
          ? JSON.parse(item.allowed_pickup_warehouse_ids || '[]').map(String)
          : [];
      setTrackingScope({
        allowedPickupWarehouseIds,
        canViewNonWarehousePickups: Boolean(item.can_view_non_warehouse_pickups),
      });
    }).catch(() => setTrackingScope({ allowedPickupWarehouseIds: [], canViewNonWarehousePickups: false }));
  }, [user?.id, user?.role]);

  const filteredRows = useMemo(() => {
    if (user?.role === 'admin' || trackingScope.allowedPickupWarehouseIds.length === 0) return rows;
    return rows.filter((row) => {
      const matchesWarehouse = row.pickupWarehouseId && trackingScope.allowedPickupWarehouseIds.includes(String(row.pickupWarehouseId));
      const isNonWarehousePickup = !row.pickupWarehouseId;
      return matchesWarehouse || (trackingScope.canViewNonWarehousePickups && isNonWarehousePickup);
    });
  }, [rows, trackingScope, user?.role]);

  const duplicateRows = useMemo(() => {
    const groups = filteredRows.reduce<Record<string, any[]>>((acc, row) => {
      const invoiceCell = String((row as any).faturalar || '').trim();
      const hasInvoice = invoiceCell !== '' && invoiceCell !== '-';
      if (!hasInvoice) return acc;
      const ettn = String(row.ettnNo || '').trim().toUpperCase();
      if (!ettn || ettn === '-') return acc;
      if (!acc[ettn]) acc[ettn] = [];
      acc[ettn].push(row);
      return acc;
    }, {});
    return Object.values(groups).filter((items) => items.length > 1).flat();
  }, [filteredRows]);

  const visibleRows = accountingViewTab === 'duplicates' ? duplicateRows : filteredRows;

  useEffect(() => {
    if (!signatureModal?.requestId || signatureModal.status === 'signed' || signatureModal.status === 'failed' || signatureModal.status === 'expired') return;
    const pollStatus = async () => {
      try {
        const response: any = await signatureService.getRequestStatus(signatureModal.requestId);
        const data = response?.data || {};
        const nextStatus = data.status || 'pending';
        setSignatureModal((current) => current ? { ...current, status: nextStatus, error: data.error || null, pageCount: data.pageCount ?? current.pageCount ?? null } : null);
        if (nextStatus === 'signed') {
          toast.success('Belge basariyla e-imzalandi');
          setSignatureModal(null);
          await loadData();
        }
      } catch (error) {
        console.error('Signature status polling failed:', error);
      }
    };
    void pollStatus();
    const timer = window.setInterval(pollStatus, 5000);
    return () => window.clearInterval(timer);
  }, [signatureModal?.requestId, signatureModal?.status]);

  useEffect(() => {
    const queuedRows = rows.filter((row) => {
      const signStatus = String(row.eSignStatus || '').trim().toLowerCase();
      const accountingStatus = String(row.accountingStatus || '').trim();
      return ['queued', 'pending'].includes(signStatus)
        && ['invoice-approval-pending', 'invoice-check-approved'].includes(accountingStatus);
    });

    if (queuedRows.length === 0) return;

    const pollStatuses = async () => {
      if (signStatusRefreshInFlightRef.current) return;
      signStatusRefreshInFlightRef.current = true;
      try {
        const response: any = await accountingService.getPreApprovalSignStatuses(queuedRows.map((row) => row.id));
        const statusRows = Array.isArray(response?.data) ? response.data : [];
        const shouldReload = statusRows.some((statusRow: any) => {
          const localRow = queuedRows.find((row) => String(row.id) === String(statusRow.shipmentId));
          if (!localRow) return false;
          const nextStatus = String(statusRow.signStatus || '').trim().toLowerCase();
          const currentStatus = String(localRow.eSignStatus || '').trim().toLowerCase();
          return nextStatus !== currentStatus && ['signed', 'failed', 'expired', 'generated'].includes(nextStatus);
        });

        if (shouldReload) {
          await loadData();
        }
      } catch (error) {
        console.error('Queued signature status polling failed:', error);
      } finally {
        signStatusRefreshInFlightRef.current = false;
      }
    };

    const timer = window.setInterval(pollStatuses, 10000);
    return () => window.clearInterval(timer);
  }, [rows]);

  const downloadSignatureBundle = async (requestId: string | number, token: string) => {
    const response = await fetch(signatureService.getRequestBundleDownloadUrl(requestId, token));
    if (!response.ok) {
      let message = 'Imza paketi indirilemedi';
      try {
        const data = await response.json();
        message = data?.message || message;
      } catch {
        // ignore json parse error
      }
      throw new Error(message);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `signature-bundle-${requestId}.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  };

  const handleRequestESign = async (row: any, signingRole: 'controller' | 'approver' = 'controller') => {
    try {
      const response: any = await signatureService.createRequest({ shipmentId: row.id, documentType: 'payment-preapproval-package', signingRole });
      const data = response?.data || {};
      const requestId = String(data.signatureRequestId || '');
      const token = String(data.oneTimeToken || '');
      if (!requestId || !token) throw new Error('Imza istegi olusturulamadi');
      window.setTimeout(() => {
        void downloadSignatureBundle(requestId, token).catch((bundleError: any) => {
          console.error('Signature bundle download failed:', bundleError);
          toast.error(bundleError?.message || 'Imza paketi indirilemedi');
        });
      }, 150);
      setSignatureModal({ shipmentId: String(row.id), shipmentNo: row.sevkKodu || '-', requestId, token, signingRole, status: 'pending', error: null });
      toast.success('E-imza istegi olusturuldu, imza paketi indiriliyor');
    } catch (error: any) {
      console.error('Create signature request failed:', error);
      toast.error(error?.message || 'E-imza istegi olusturulamadi');
    }
  };

  const handleBulkRequestESign = async (rows: any[], signingRole: 'controller' | 'approver') => {
    const targets = rows.filter((row) => {
      const status = String(row.accountingStatus || '').trim();
      return signingRole === 'controller'
        ? status === 'invoice-approval-pending'
        : status === 'invoice-check-approved';
    });
    if (targets.length === 0) {
      toast.error('E-imza icin uygun kayit bulunamadi');
      return;
    }

    try {
      setBulkESignModal({ count: targets.length, role: signingRole });
      const response: any = await signatureService.downloadBulkRequestBundle({
        shipmentIds: targets.map((row) => row.id),
        documentType: 'payment-preapproval-package',
        signingRole,
      });
      if (response?.mode === 'blob' && response?.blob) {
        const objectUrl = URL.createObjectURL(response.blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = `signature-bundle-bulk-${signingRole}.zip`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
      } else {
        const payload = response?.payload || {};
        const rawDownloadUrl = String(payload?.data?.downloadUrl || '').trim();
        const token = String(payload?.data?.downloadToken || '');
        const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
        const downloadUrl = rawDownloadUrl
          ? (rawDownloadUrl.startsWith('http://') || rawDownloadUrl.startsWith('https://') ? rawDownloadUrl : `${apiBase}${rawDownloadUrl.startsWith('/') ? '' : '/'}${rawDownloadUrl}`)
          : (token ? signatureService.getBulkRequestBundleDownloadUrl(token) : '');
        if (!downloadUrl) throw new Error('Toplu imza paketi indirilemedi');
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = `signature-bundle-bulk-${signingRole}.zip`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      await loadData();
      toast.success(`${targets.length} kayit icin imza istek paketi indiriliyor.`);
    } finally {
      setBulkESignModal(null);
    }
  };

  const handleOpenBulkEmailModal = async (rows: any[]) => {
    const targets = rows.filter((row) => String(row.accountingStatus || '').trim() === 'payment-pending');
    if (targets.length === 0) {
      toast.error('E-posta icin uygun kayit bulunamadi');
      return;
    }
    setBulkEmailRows(targets);
    setBulkEmailRecipientEmail('');
    setBulkEmailModalOpen(true);
  };

  const handleBulkResetESign = async (rows: any[]) => {
    const targets = rows.filter((row) => {
      const status = String(row.accountingStatus || '').trim();
      return ['invoice-approval-pending', 'invoice-check-approved'].includes(status);
    });
    if (targets.length === 0) {
      toast.error('E-imza reddi icin uygun kayit bulunamadi');
      return;
    }
    try {
      setBulkResetModal({ count: targets.length });
      const response: any = await accountingService.bulkResetPreApprovalSign(targets.map((row) => row.id));
      toast.success(response?.message || 'Toplu e-imza kayitlari reddedildi');
      await loadData();
    } catch (error: any) {
      console.error('Bulk reset e-sign failed:', error);
      toast.error(error?.message || 'Toplu e-imza reddetme islemi yapilamadi');
    } finally {
      setBulkResetModal(null);
    }
  };

  const handleRefreshPreApprovalPdf = async (row: any) => {
    try {
      await accountingService.generatePreApprovalPackage(row.id);
      toast.success('Ön onay PDF güncellendi');
      await loadData();
    } catch (error: any) {
      console.error('Pre-approval PDF refresh failed:', error);
      toast.error(error?.message || 'Ön onay PDF güncellenemedi');
    }
  };

  const handleBulkRefreshPreApprovalPdf = async (rows: any[]) => {
    const targets = rows.filter((row) => ['invoice-approval-pending', 'invoice-check-approved'].includes(String(row.accountingStatus || '').trim()));
    if (targets.length === 0) {
      toast.error('Ön onay PDF güncelleme icin uygun kayit bulunamadi');
      return;
    }
    try {
      const response: any = await accountingService.bulkGeneratePreApprovalPackages(targets.map((row) => row.id));
      const updated = Number(response?.data?.updated || 0);
      const skipped = Array.isArray(response?.data?.skipped) ? response.data.skipped.length : 0;
      toast.success(`${updated} kayit icin ön onay PDF guncellendi${skipped ? `, ${skipped} kayit atlandi` : ''}`);
      await loadData();
    } catch (error: any) {
      console.error('Bulk pre-approval PDF refresh failed:', error);
      toast.error(error?.message || 'Toplu ön onay PDF guncellenemedi');
    }
  };

  const handleRepairInvoiceLinks = async () => {
    try {
      setRepairingInvoiceLinks(true);
      const response: any = await accountingService.repairInvoiceLinks();
      toast.success(response?.message || 'Fatura geçiş kayıtları düzeltildi');
      await loadData();
    } catch (error: any) {
      console.error('Repair invoice links failed:', error);
      toast.error(error?.message || 'Fatura geçiş kayıtları düzeltilemedi');
    } finally {
      setRepairingInvoiceLinks(false);
    }
  };

  const handleScanInvoiceEttn = async () => {
    try {
      setScanningEttn(true);
      const response: any = await accountingService.scanInvoiceEttn(filteredRows.map((row) => row.id), true);
      if (response?.data?.alreadyRunning) {
        toast.info('ETTN taraması zaten devam ediyor');
      } else {
        toast.success('ETTN taraması başlatıldı, sonuçlar geldikçe listeye yansıyacak');
      }
      pollEttnScanStatus();
    } catch (error: any) {
      console.error('Scan invoice ETTN failed:', error);
      toast.error(error?.message || 'ETTN kontrolü başlatılamadı');
      setScanningEttn(false);
    }
  };

  const pollEttnScanStatus = async () => {
    try {
      const statusResponse: any = await accountingService.getEttnScanStatus();
      const status = statusResponse?.data || {};
      if (status?.running) {
        window.setTimeout(() => void pollEttnScanStatus(), 3000);
        return;
      }
      setScanningEttn(false);
      const duplicateCount = Array.isArray(status?.duplicates) ? status.duplicates.length : 0;
      toast.success(`${status?.updated || 0} kayıt için ETTN güncellendi${duplicateCount ? `, ${duplicateCount} mükerrer ETTN bulundu` : ''}`);
      await loadData();
      if (duplicateCount > 0) setAccountingViewTab('duplicates');
    } catch (error: any) {
      console.error('ETTN scan status poll failed:', error);
      setScanningEttn(false);
    }
  };

  const handleSubmitBulkEmail = async () => {
    if (!bulkEmailRecipientEmail.trim()) {
      toast.error('Lutfen alici e-posta adresini girin');
      return;
    }
    try {
      setBulkEmailSending(true);
      const response: any = await accountingService.bulkEmailSignedPdf({
        shipmentIds: bulkEmailRows.map((row) => row.id),
        recipientEmail: bulkEmailRecipientEmail.trim(),
      });
      toast.success(response?.message || 'E-posta gonderildi');
      setBulkEmailModalOpen(false);
    } catch (error: any) {
      console.error('Bulk signed pdf email failed:', error);
      toast.error(error?.message || 'E-posta gonderilemedi');
    } finally {
      setBulkEmailSending(false);
    }
  };

  const rowsWithSignatureAction = useMemo(() => visibleRows.map((row) => ({
    ...row,
    eSignAction: row.accountingStatus === 'invoice-approval-pending' || row.accountingStatus === 'invoice-check-approved'
      ? 'E-İmzala'
      : '-',
    __requestESign: row.accountingStatus === 'invoice-check-approved'
      ? () => void handleRequestESign(row, 'approver')
      : row.accountingStatus === 'invoice-approval-pending'
        ? () => void handleRequestESign(row, 'controller')
        : undefined,
  })), [visibleRows]);

  return (
    <>
    <div className="mb-3 flex justify-end">
      <div className="flex flex-wrap items-center gap-2">
      <div className="flex rounded-xl bg-slate-100 p-1">
        <button type="button" onClick={() => setAccountingViewTab('all')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${accountingViewTab === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Tümü</button>
        <button type="button" onClick={() => setAccountingViewTab('duplicates')} className={`rounded-lg px-3 py-2 text-sm font-semibold ${accountingViewTab === 'duplicates' ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-500'}`}>Mükerrer ETTN ({duplicateRows.length})</button>
      </div>
      <button type="button" onClick={() => void handleScanInvoiceEttn()} disabled={scanningEttn} className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60">
        {scanningEttn ? 'OCR ile Faturaları Kontrol Ediliyor...' : 'OCR ile Yüklenen Faturaları Kontrol Et'}
      </button>
      <button type="button" onClick={() => void handleRepairInvoiceLinks()} disabled={repairingInvoiceLinks} className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60">
        {repairingInvoiceLinks ? 'Fatura Kayıtları Düzeltiliyor...' : 'Geçmiş Fatura ve Toplu Fatura Gruplarını Düzelt'}
      </button>
      </div>
    </div>
    <FlexibleTracking
      title="Finansal İşlemler"
      subtitle="Fatura ve ödeme süreçlerini FlexibleTracking görünümünde yönetin."
      settingsKeyScope="accounting_workflow"
      externalRows={rowsWithSignatureAction as any}
      externalLoading={loading}
      defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS as any}
      flowStatusOptions={[...FINANCE_FLOW_OPTIONS] as any}
      hideArchiveTab
      hideCreateActions
      forceBlackTableText
      constrainTableToViewport
       onBulkRequestESign={handleBulkRequestESign as any}
       onBulkResetESign={handleBulkResetESign as any}
       onBulkSendSignedPdfEmail={handleOpenBulkEmailModal as any}
       onBulkAccountingStatusUpdate={loadData}
       onBulkRefreshPreApprovalPdf={handleBulkRefreshPreApprovalPdf as any}
      onRefreshPreApprovalPdf={handleRefreshPreApprovalPdf as any}
    />
    {signatureModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSignatureModal(null)}>
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="text-lg font-bold text-gray-900">E-Imza Bekleniyor</div>
            <div className="mt-1 text-sm text-gray-500">Belge: {signatureModal.shipmentNo}</div>
            <div className="mt-1 text-xs text-gray-500">Rol: {signatureModal.signingRole === 'approver' ? 'Onaylayan' : 'Kontrol Eden'}</div>
          </div>
          <div className="space-y-4 px-5 py-4 text-sm text-gray-700">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
              1. request.json ve JAR tek ZIP dosyasi olarak indirilir.
              <br />2. ZIP dosyasini acin ve icindeki JAR dosyasina cift tiklayin.
              <br />3. Imza tamamlaninca durum otomatik guncellenecek.
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => void downloadSignatureBundle(signatureModal.requestId, signatureModal.token).catch((error: any) => { console.error('Manual signature bundle download failed:', error); toast.error(error?.message || 'Imza paketi indirilemedi'); })} className="rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white">Imza Paketini Indir</button>
              <a href={signatureService.getRequestPreviewUrl(signatureModal.requestId, signatureModal.token)} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700">PDF Onizle{signatureModal.pageCount ? ` (${signatureModal.pageCount} sayfa)` : ''}</a>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <div><span className="font-semibold">Request ID:</span> {signatureModal.requestId}</div>
              <div><span className="font-semibold">Durum:</span> {signatureModal.status}</div>
              {signatureModal.error ? <div className="mt-2 text-red-600">Hata: {signatureModal.error}</div> : null}
            </div>
          </div>
          <div className="flex justify-end border-t border-gray-200 px-5 py-4">
            <button type="button" onClick={() => setSignatureModal(null)} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700">Kapat</button>
          </div>
        </div>
      </div>
    )}
    {bulkEmailModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setBulkEmailModalOpen(false)}>
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="text-lg font-bold text-gray-900">Toplu Imzali PDF E-Posta Gonder</div>
            <div className="mt-1 text-sm text-gray-500">{bulkEmailRows.length} secili kayit icin imzali PDF eki tek aliciya gonderilecek. Tek fatura gruplarinda tek PDF gonderilir.</div>
          </div>
          <div className="space-y-4 px-5 py-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Alici E-Posta</label>
              <input type="email" value={bulkEmailRecipientEmail} onChange={(event) => setBulkEmailRecipientEmail(event.target.value)} placeholder="ornek@email.com" className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500" />
            </div>
          </div>
          <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
            <button type="button" onClick={() => setBulkEmailModalOpen(false)} className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700">Kapat</button>
            <button type="button" onClick={() => void handleSubmitBulkEmail()} disabled={bulkEmailSending} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold text-white disabled:opacity-60">{bulkEmailSending ? 'Gonderiliyor...' : 'Gonder'}</button>
          </div>
        </div>
      </div>
    )}
    {bulkResetModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="text-lg font-bold text-gray-900">E-İmza Reddediliyor</div>
            <div className="mt-1 text-sm text-gray-500">Secilen kayitlar e-imza surecinden geri aliniyor.</div>
          </div>
          <div className="px-5 py-5">
            <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <Loader2 className="h-5 w-5 animate-spin" />
              <div>{bulkResetModal.count} kayit icin e-imza kayitlari reddediliyor...</div>
            </div>
          </div>
        </div>
      </div>
    )}
    {bulkESignModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="text-lg font-bold text-gray-900">Toplu E-İmza Paketi Hazırlanıyor</div>
            <div className="mt-1 text-sm text-gray-500">Indirme baslayana kadar lutfen bekleyin.</div>
          </div>
          <div className="px-5 py-5">
            <div className="flex items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-900">
              <Loader2 className="h-5 w-5 animate-spin" />
              <div>{bulkESignModal.count} kayit icin {bulkESignModal.role === 'approver' ? 'son onay' : 'kontrol'} e-imza paketi hazirlaniyor...</div>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
